import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { api, isApiError } from '../../api';
import { Badge } from '../../components/Badge';
import { Callout, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, Loading } from '../../components/States';
import { formatDate, formatDateTime } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { useApi } from '../../lib/useApi';
import { ActorName, CustomerRef, domainHref, DomainRef, RegistryRef, SubjectRef, TxRef, useVocab, WorkflowRef } from '../domains/refs';
import { COMMAND_STATE, DOSSIER, humanize, StatusBadge, TRANSFER } from '../domains/status';
import { EvidenceView, RelatedEvents } from './shared';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

function Party({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border p-4">
      <div className="mb-3 text-sm font-semibold">{title}</div>
      {children}
    </div>
  );
}

/** `/registrant-changes/:id`: one change of registrant with both parties, confirmations and registry command. */
export function RegistrantChangePage() {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  const vocab = useVocab();
  const L = useLabels();
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getRegistrantChange(id, { signal }), [id]);
  const title = result.status === 'success' ? t('changes.detailTitle', { domain: result.data.unicode_name }) : t('changes.title');
  useDocumentTitle(title);
  const crumbs = [{ label: t('changes.crumb') }, { label: t('changes.title'), to: '/registrant-changes' }];

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('changes.title')} crumbs={crumbs} />
        <Loading rows={4} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={t('changes.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('changes.notFound') : t('changes.loadFailed')} />
      </>
    );
  }
  const x = result.data;
  const failed = x.status === 'REJECTED' || x.status === 'EXPIRED' || x.status === 'CANCELLED';
  const verificationTone = x.to_verification_status === 'VERIFIED' ? 'ok' : x.to_verification_status === 'FAILED' || x.to_verification_status === 'REJECTED' ? 'crit' : 'pending';

  return (
    <>
      <PageHeader
        title={title}
        crumbs={[...crumbs, { label: x.unicode_name }]}
        subtitle={t('changes.detailSubtitle', { domain: x.unicode_name })}
        actions={<StatusBadge map={TRANSFER} value={x.status} />}
      />
      {x.failure_reason ? (
        <Callout tone={failed && x.status !== 'CANCELLED' ? 'crit' : 'info'} title={t('changes.failure')} className="mb-6">
          <span title={x.failure_reason}>{vocab('failure', x.failure_reason)}</span>
        </Callout>
      ) : null}
      <div className="grid grid-cols-1 gap-6">
        <Section title={t('changes.overview')}>
          <KeyValue
            columns={3}
            rows={[
              [t('changes.domain'), <DomainRef key="d" ascii={x.ascii_name} unicode={x.unicode_name} id={x.domain_id} tab="transfers" />],
              [t('changes.status'), <StatusBadge key="s" map={TRANSFER} value={x.status} />],
              [t('changes.customer'), <CustomerRef key="c" id={x.customer_id} name={x.customer_name} />],
              [t('changes.registry'), <RegistryRef key="r" code={x.registry_code} name={x.registry_name} />],
              [t('changes.requested'), formatDateTime(x.requested_at)],
              [t('changes.requestedBy'), <ActorName key="rb" type={x.requested_by_type} name={x.requested_by_name} />],
              x.completed_at ? [t('changes.completedAt'), formatDateTime(x.completed_at)] : null,
              [t('changes.optOut'), x.transfer_lock_opt_out ? t('changes.optOutYes') : t('changes.optOutNo')],
              [t('common.id'), <CopyText key="id" value={x.id} />],
            ]}
          />
        </Section>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Party title={t('changes.from')}>
            <KeyValue
              rows={[
                [t('contacts.subjectName'), <SubjectRef key="s" id={x.from_subject_id} name={x.from_display_name} type={x.from_subject_type} />],
                [t('changes.version'), x.from_version_number ? t('common.version', { n: x.from_version_number }) : '—'],
                [t('changes.country'), x.from_country_code ?? '—'],
              ]}
            />
          </Party>
          <Party title={t('changes.to')}>
            <KeyValue
              rows={[
                [t('contacts.subjectName'), <SubjectRef key="s" id={x.to_subject_id} name={x.to_display_name} type={x.to_subject_type} />],
                [t('changes.version'), t('common.version', { n: x.to_version_number })],
                [t('changes.country'), x.to_country_code ?? '—'],
                [
                  t('changes.verification'),
                  x.to_verification_status ? (
                    <span key="v" className="flex flex-col gap-0.5">
                      <Badge tone={verificationTone} title={x.to_verification_status}>
                        {tr(`subject.verificationStatuses.${x.to_verification_status}`, { defaultValue: humanize(x.to_verification_status) })}
                      </Badge>
                      {x.to_verification_method ? <span className="text-xs text-muted-foreground">{vocab('method', x.to_verification_method)}</span> : null}
                    </span>
                  ) : (
                    '—'
                  ),
                ],
                x.to_verified_at ? [t('changes.verifiedAt'), formatDateTime(x.to_verified_at)] : null,
                x.to_verification_expires_at ? [t('changes.verificationExpires'), formatDate(x.to_verification_expires_at)] : null,
                [
                  t('changes.newDossier'),
                  x.new_dossier_id ? (
                    <span key="nd" className="flex flex-wrap items-center gap-2">
                      <EntityRef label={t('common.version', { n: x.new_dossier_version ?? 1 })} to={`/dossiers/${encodeURIComponent(x.new_dossier_id)}`} id={x.new_dossier_id} />
                      {x.new_dossier_status ? <StatusBadge map={DOSSIER} value={x.new_dossier_status} /> : null}
                    </span>
                  ) : (
                    t('changes.noNewDossier')
                  ),
                ],
              ]}
            />
          </Party>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Section title={t('changes.follow')}>
            <KeyValue
              rows={[
                [
                  t('changes.workflow'),
                  <WorkflowRef key="w" id={x.workflow_id} type={x.workflow_type} state={x.workflow_state} sub={x.workflow_step ? t('changes.step', { step: L.workflowStep(x.workflow_step) }) : undefined} />,
                ],
                [
                  t('changes.command'),
                  x.registry_transaction_id ? (
                    <span key="tx" className="flex flex-wrap items-center gap-2">
                      <TxRef id={x.registry_transaction_id} operation={x.registry_operation} sub={x.registry_cltrid} />
                      {x.registry_transaction_state ? <StatusBadge map={COMMAND_STATE} value={x.registry_transaction_state} /> : null}
                    </span>
                  ) : (
                    <span key="tx" className="text-sm text-muted-foreground">
                      {t('changes.noCommand')}
                    </span>
                  ),
                ],
              ]}
            />
          </Section>
          <Section title={t('changes.evidence')} description={t('changes.evidenceDescription')}>
            <EvidenceView evidence={x.authorization_evidence} empty={t('transfers.noEvidence')} />
          </Section>
        </div>

        <Section flush title={t('changes.events')}>
          <RelatedEvents events={x.events} empty={t('changes.noEvents')} historyHref={domainHref(x.ascii_name, 'history')} />
        </Section>
      </div>
    </>
  );
}
